import useEmblaCarousel from 'embla-carousel-react';
import { useEffect, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { useDispatch, useSelector } from 'react-redux';
import Slider from 'react-slick';
import { useToggle } from 'react-use';

import { IState } from 'models/reducers/i-state.interface';

import {
  fetchNftOfTheDayAction,
  fetchNftOfTheDayAssetsAction,
  resetNftOfTheDayDataAction,
} from 'actions/nft-of-the-day.actions';

import DotButton from 'components/carousel/DotButton';
import { useDotButton } from 'components/carousel/DotButton.hooks';
import Page from 'components/page/Page';
import PageWrapper from 'components/pageWrapper/PageWrapper';
import SocialsBar from 'components/socials/SocialsBar';

import PromotionsBottomBanner from './promotionsBottomBanner/PromotionsBottomBanner';
import PromotionsMiddleBanner from './promotionsMiddleBanner/PromotionsMiddleBanner';
import PromotionsNews from './promotionsNews/PromotionsNews';
import PromotionsNftCard from './promotionsNftCard/PromotionsNftCard';
import styles from './PromotionsRoute.module.scss';
import PromotionsTopBanner from './promotionsTopBanner/PromotionsTopBanner';
import { getPromotionsService } from './service';
import { IBanner, INews } from './types';

const PromotionsRoute = () => {
  const dispatch = useDispatch();
  const { offers, assetsLoading, updateToken } = useSelector((state: IState) => state.nftOfTheDay);

  useEffect(() => {
    dispatch(fetchNftOfTheDayAction(new AbortController().signal));
  }, [dispatch, updateToken]);

  useEffect(() => {
    if (offers.length) {
      dispatch(fetchNftOfTheDayAssetsAction(offers.map(({ asset_id }) => asset_id)));
    }
  }, [dispatch, offers]);

  useEffect(() => {
    return () => {
      dispatch(resetNftOfTheDayDataAction());
    };
  }, [dispatch]);

  const [loading, onLoading] = useToggle(true);
  const [news, setNews] = useState<INews[]>([]);
  const [topBanners, setTopBanners] = useState<IBanner[]>([]);
  const [promotionsBanners, setPromotionsBanners] = useState<IBanner[]>([]);
  const [communityBanner, setCommunityBanner] = useState<IBanner>();
  const [eventsBanner, setEventsBanner] = useState<IBanner>();

  useEffect(() => {
    getPromotionsService(new AbortController().signal)
      .then((response) => {
        setNews(response.data.news);
        setTopBanners(response.data.top_banners);
        setPromotionsBanners(response.data.promotions_banners);
        setCommunityBanner(response.data.community_banner);
        setEventsBanner(response.data.events_banner);
      })
      .finally(() => setTimeout(() => onLoading(false), 1000));
  }, []);

  const [emblaRef, emblaApi] = useEmblaCarousel();
  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(emblaApi);

  return (
    <Page pageBackground="dark" loading={loading || assetsLoading}>
      <Scrollbars style={{ width: '100%', height: '100%' }}>
        <PageWrapper landingFooter homeFooter={false}>
          <div className="tw-mx-auto tw-mb-10 md:tw-mb-20 md:tw-mt-10 md:tw-max-w-screen-md lg:tw-max-w-screen-lg xl:tw-max-w-screen-xl">
            <Slider
              dots
              speed={300}
              rows={1}
              arrows={false}
              infinite={false}
              dotsClass="slick-dots"
              className={styles['slider']}
              slidesToShow={1}
              useTransform={false}
            >
              {topBanners.map((banner) => (
                <PromotionsTopBanner key={banner.order} {...banner} />
              ))}
            </Slider>
          </div>
          <div className="tw-mx-auto tw-flex tw-flex-col tw-gap-10 tw-px-5 tw-py-10 md:tw-max-w-screen-md lg:tw-max-w-screen-lg lg:tw-gap-20 lg:tw-px-0 xl:tw-max-w-screen-xl">
            <div className="tw-grid md:tw-grid-cols-[1fr_2fr] md:tw-gap-x-10 lg:tw-gap-x-20">
              <PromotionsNftCard />
              <PromotionsNews news={news} />
            </div>
            <div className="tw-mb-10">
              <h2 className="tw-mb-5 tw-text-center tw-text-[32px]/[28px] tw-uppercase tw-text-white lg:tw-mb-10 lg:tw-text-5xl">
                Promotions
              </h2>
              <div className={styles['embla']} ref={emblaRef}>
                <div className={styles['embla__container']}>
                  {promotionsBanners.map((banner) => (
                    <div key={banner.order} className={styles['embla__slide']}>
                      <PromotionsMiddleBanner {...banner} />
                    </div>
                  ))}
                </div>
                <div className={styles['embla__controls']}>
                  {scrollSnaps.length > 1 && (
                    <div className={styles['embla__dots']}>
                      {scrollSnaps.map((_, index) => (
                        <DotButton
                          key={index}
                          onClick={() => onDotButtonClick(index)}
                          className={index === selectedIndex ? styles['embla__dot--selected'] : styles['embla__dot']}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="tw-mx-auto">
              <h2 className="tw-mb-10 tw-text-center tw-text-[28px]/[28px] tw-uppercase tw-text-turquoise">Socials</h2>
              <SocialsBar
                order={[
                  { type: 'telegram', icon: 'gradient' },
                  { type: 'twitter', icon: 'gradient' },
                  { type: 'discord', icon: 'gradient' },
                  { type: 'youtube', icon: 'gradient' },
                  { type: 'instagram', icon: 'gradient' },
                  { type: 'facebook', icon: 'gradient' },
                ]}
                className="tw-ml-0 tw-flex tw-list-none tw-items-center tw-gap-x-6 tw-pl-0"
                iconClassName={styles['social-icon']}
              />
            </div>
            <div className="-tw-mt-2.5 tw-grid tw-gap-10 tw-pt-2.5 lg:tw-grid-cols-[1fr_1fr]">
              {communityBanner && <PromotionsBottomBanner {...communityBanner} />}
              {eventsBanner && <PromotionsBottomBanner {...eventsBanner} />}
            </div>
          </div>
        </PageWrapper>
      </Scrollbars>
    </Page>
  );
};

export default PromotionsRoute;
